/**
 * Structure-type-specific dimension configuration for SolidMeshPanel.
 *
 * Holds the per-axis label, the unit the builder or viewer boundary expects, and
 * the unit the input actually shows. Metric sites display the base unit itself;
 * feet sites display every axis in the site unit.
 */

import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import type {
	SolidMeshAxisUnitConfig,
	SolidMeshBaseDistanceUnit,
	SolidMeshDimensionConfig,
	SolidMeshUnitTransform,
} from "./solidMeshUnit.types";

/** Decimal places shown for dimension fields, regardless of the display unit. */
export const DIMENSION_DECIMALS = 2;

/** Per-axis base units, i.e. what the existing builder and viewer boundaries expect. */
const BASE_UNITS: Readonly<
	Record<string, Readonly<Record<"x" | "y" | "z", SolidMeshBaseDistanceUnit>>>
> = {
	[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: { x: "mm", y: "m", z: "mm" },
	[SIMPLE_STRUCTURE_TYPE.PIPE]: { x: "mm", y: "mm", z: "m" },
	[SIMPLE_STRUCTURE_TYPE.MANHOLE]: { x: "mm", y: "mm", z: "m" },
	[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: { x: "m", y: "m", z: "m" },
};

const DEFAULT_BASE_UNITS = { x: "m", y: "m", z: "m" } as const;

/** Per-axis labels. Resolved through i18n so no unit text is embedded. */
function getLabels(structureType: string, t: (key: string) => string) {
	switch (structureType) {
		case SIMPLE_STRUCTURE_TYPE.BOX_CULVERT:
			return {
				x: t("CULVERT_WIDTH"),
				y: t("CULVERT_LENGTH"),
				z: t("CULVERT_HEIGHT"),
			};
		case SIMPLE_STRUCTURE_TYPE.PIPE:
			return {
				x: t("PIPE_DIAMETER"),
				y: t("PIPE_DIAMETER"),
				z: t("PIPE_LENGTH"),
			};
		case SIMPLE_STRUCTURE_TYPE.RETAINING_WALL:
			return {
				x: t("WALL_WIDTH"),
				y: t("WALL_THICKNESS"),
				z: t("WALL_HEIGHT"),
			};
		case SIMPLE_STRUCTURE_TYPE.MANHOLE:
			return {
				x: t("MANHOLE_DIAMETER"),
				y: t("MANHOLE_DIAMETER"),
				z: t("MANHOLE_DEPTH"),
			};
		default:
			return { x: "X", y: "Y", z: "Z" };
	}
}

/** Builds one axis config from its base unit and the current site unit. */
function buildAxisConfig(
	label: string,
	baseUnit: SolidMeshBaseDistanceUnit,
	unitTransform: Pick<
		SolidMeshUnitTransform,
		"isMetricDistance" | "distanceLabel"
	>,
): SolidMeshAxisUnitConfig {
	const isMetric = unitTransform.isMetricDistance.value;
	return {
		label,
		baseUnit,
		displayUnit: isMetric ? baseUnit : "site",
		suffix: isMetric ? baseUnit : unitTransform.distanceLabel.value,
	};
}

/**
 * Returns per-axis dimension settings for a structure type.
 * @param structureType - e.g. "box-culvert", "pipe", "retaining-wall", "manhole"
 * @param t - i18n translate function
 * @param unitTransform - SolidMesh unit boundary used to resolve the display unit
 */
export function getDimensionConfig(
	structureType: string,
	t: (key: string) => string,
	unitTransform: Pick<
		SolidMeshUnitTransform,
		"isMetricDistance" | "distanceLabel"
	>,
): SolidMeshDimensionConfig {
	const labels = getLabels(structureType, t);
	const baseUnits = BASE_UNITS[structureType] ?? DEFAULT_BASE_UNITS;
	return {
		x: buildAxisConfig(labels.x, baseUnits.x, unitTransform),
		y: buildAxisConfig(labels.y, baseUnits.y, unitTransform),
		z: buildAxisConfig(labels.z, baseUnits.z, unitTransform),
	};
}

/**
 * Formats an internal metre value for read-only display using an axis config.
 * @param meters - Value in internal metres
 * @param axis - Axis config providing the display unit and suffix
 * @param convertLength - Length conversion from the SolidMesh unit boundary
 */
export function formatDimensionValue(
	meters: number,
	axis: SolidMeshAxisUnitConfig,
	convertLength: SolidMeshUnitTransform["convertLength"],
): string {
	// Convert at full precision; toFixed below is the only rounding step.
	const displayed = convertLength(meters, "m", axis.displayUnit);
	return `${displayed.toFixed(DIMENSION_DECIMALS)} ${axis.suffix}`;
}
